import { useState } from 'react';
import { Lock, CircleAlert as AlertCircle, Loader as Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { validatePassword, PASSWORD_MIN_LENGTH, PASSWORD_HINT } from '../../utils/passwordPolicy';
import { MosLockup } from '../brand/MosMark';

// Shown after a user clicks the invite link (or password-reset link) and
// supabase-js mints a session from the URL hash. They have a live session
// but no real password yet, so we block the rest of the app behind this
// screen and force them to set one before continuing.
//
// Detected via the `type=invite` / `type=recovery` parameter in the URL
// hash — see AuthContext for the capture logic.
export function SetInitialPasswordScreen() {
  const { user, employee, requiresPasswordSetup, clearPasswordSetupRequirement, updatePassword, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isRecovery = requiresPasswordSetup === 'recovery';
  const heading = isRecovery ? 'Set a new password' : 'Welcome to Marcstone OS';
  const subheading = isRecovery
    ? 'Choose a new password to finish resetting your account.'
    : 'Choose a password to finish setting up your account.';
  const ctaLabel = isRecovery ? 'Update password' : 'Set password & continue';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const v = validatePassword(password, {
      isAdmin: employee?.accessTier === 3,
      blockContaining: [employee?.name, employee?.email ?? user?.email],
    });
    if (!v.ok) {
      setError(v.error ?? 'Invalid password.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(password);
      clearPasswordSetupRequirement();
      toast.success(isRecovery ? 'Password updated' : 'Password set — welcome!');
    } catch (err: any) {
      console.error('Set password error:', err);
      setError(err.message || 'Failed to set password.');
      toast.error(err.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    // If the user backs out of setting a password, sign them out — leaving
    // them in the app with a session but no real password is worse than
    // sending them back to the login screen.
    try {
      await signOut();
    } catch {
      // best-effort; clearing local state below is the important part
    }
    clearPasswordSetupRequirement();
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: 'Inter, Helvetica, sans-serif',
    backgroundColor: '#ffffff',
    color: '#2c2a29',
    border: '1px solid #d1d5dc',
    borderRadius: '10px',
    height: '50px',
    width: '100%',
    paddingLeft: '40px',
    paddingRight: '44px',
    fontSize: '16px',
    outline: 'none',
    boxShadow: 'none',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', padding: '16px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '448px' }}>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginBottom: '32px' }}>
          <MosLockup height={60} style={{ marginBottom: '20px' }} />

          <h1 style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 700, color: '#101828', fontSize: '30px', lineHeight: '36px', textAlign: 'center', margin: '0 0 8px 0' }}>
            {heading}
          </h1>

          <p style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 400, color: '#4a5565', fontSize: '16px', lineHeight: '24px', textAlign: 'center', margin: 0 }}>
            {subheading}
          </p>

          {user?.email && (
            <p style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 500, color: '#2c2a29', fontSize: '14px', lineHeight: '20px', textAlign: 'center', margin: '12px 0 0 0' }}>
              {user.email}
            </p>
          )}
        </div>

        <div style={{ width: '100%', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0px 8px 10px -6px rgba(0,0,0,0.1), 0px 20px 25px -5px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: '33px' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label
                  htmlFor="new-password"
                  style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 500, color: '#364153', fontSize: '14px', lineHeight: '20px' }}
                >
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 1 }}>
                    <Lock style={{ width: '16px', height: '16px' }} />
                  </span>
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={employee?.accessTier === 3 ? 12 : PASSWORD_MIN_LENGTH}
                    autoComplete="new-password"
                    placeholder={`At least ${employee?.accessTier === 3 ? 12 : PASSWORD_MIN_LENGTH} characters`}
                    disabled={loading}
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#9ca3af',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {showPassword
                      ? <EyeOff style={{ width: '16px', height: '16px' }} />
                      : <Eye style={{ width: '16px', height: '16px' }} />}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label
                  htmlFor="confirm-password"
                  style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 500, color: '#364153', fontSize: '14px', lineHeight: '20px' }}
                >
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 1 }}>
                    <Lock style={{ width: '16px', height: '16px' }} />
                  </span>
                  <input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={employee?.accessTier === 3 ? 12 : PASSWORD_MIN_LENGTH}
                    autoComplete="new-password"
                    placeholder="Re-type your password"
                    disabled={loading}
                    style={inputStyle}
                  />
                </div>
              </div>

              <p style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 400, color: '#6a7282', fontSize: '12px', lineHeight: '16px', margin: '-12px 0 0 0' }}>
                {PASSWORD_HINT}
              </p>

              {error && (
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <AlertCircle style={{ width: '20px', height: '20px', color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ fontSize: '14px', color: '#991b1b', margin: 0 }}>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  height: '48px',
                  width: '100%',
                  backgroundColor: loading ? 'rgba(44,42,41,0.5)' : '#2c2a29',
                  borderRadius: '10px',
                  fontFamily: 'Inter, Helvetica, sans-serif',
                  fontWeight: 500,
                  color: '#ffffff',
                  fontSize: '16px',
                  lineHeight: '24px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#3d3b3a'; }}
                onMouseLeave={(e) => { if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#2c2a29'; }}
              >
                {loading ? (
                  <>
                    <Loader2 style={{ width: '20px', height: '20px' }} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  ctaLabel
                )}
              </button>
            </form>

            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '24px', paddingTop: '24px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6a7282',
                  fontSize: '13px',
                  fontFamily: 'Inter, Helvetica, sans-serif',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Sign out and use a different account
              </button>
            </div>
          </div>
        </div>

        <p style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 400, color: '#6a7282', fontSize: '14px', lineHeight: '20px', textAlign: 'center', marginTop: '20px' }}>
          Need help? Contact your system administrator
        </p>
      </div>
    </div>
  );
}
